"use client";

import React, { useCallback } from "react";
import { AdGallery } from "@/models/AdDetailModel";
import { Box, styled } from "@mui/material";
import {
  AD_DETAILS_2_COLUMN_MAX_WIDTH,
  AD_DETAILS_BORDER_RADIUS,
} from "@/app/layout/LayoutConstants";
import { useState } from "react";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import theme from "@/app/layout/theme";
import { useDeviceNavigation } from "@/app/layout/useDeviceNavigation";
import ThumbnailsCarousel from "./ThumbnailsCarousel";
import { DarkIconButton } from "@/app/shared/DarkIconButton";
import { GalleryCarousel } from "./GalleryCarousel";
import { useCarouselState } from "@/app/layout/useCarouselState";

const ThumbnailSpacingTwoColumn = 4;

function ThumbnailContainer({ children }: { children: React.ReactNode }) {
  return (
    <Box
      sx={(theme) => ({
        paddingLeft: ThumbnailSpacingTwoColumn,
        paddingBottom: ThumbnailSpacingTwoColumn,
        [theme.breakpoints.down(AD_DETAILS_2_COLUMN_MAX_WIDTH)]: {
          padding: 0,
        },
      })}
    >
      <Box
        sx={{
          position: "relative",
          width: "100%",
          height: "100%",
          backgroundColor: "grey.200",
          borderRadius: AD_DETAILS_BORDER_RADIUS,
          cursor: "pointer",
          overflow: "hidden",
        }}
      >
        {children}
      </Box>
    </Box>
  );
}

const GalleryThumbnail = styled(Box)({
  cursor: "pointer",
  position: "relative",
  height: "100%",
});

export default function DetailsGallery({
  adId,
  gallery,
}: {
  adId: string;
  gallery: AdGallery;
}) {
  const { activeIndex, setActiveIndex } = useCarouselState({ key: adId });
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [isZoomed, setIsZoomed] = useState(false);
  const handleSetIsFullScreen = useCallback(
    (newIsFullScreen: boolean) => {
      if (isFullScreen === newIsFullScreen) {
        return;
      }
      setIsFullScreen(newIsFullScreen);
      if (newIsFullScreen) {
        window.history.pushState(null, "");
      } else {
        window.history.back();
      }
    },
    [isFullScreen],
  );
  useDeviceNavigation({
    isFullScreen: isFullScreen,
    onBackButton: useCallback(() => setIsFullScreen(false), []),
    onClose: useCallback(
      () => handleSetIsFullScreen(false),
      [handleSetIsFullScreen],
    ),
    onNavigation: useCallback(
      (delta) => {
        setActiveIndex((prev) => {
          const newIndex = prev + delta;
          if (newIndex < 0) {
            return 0;
          }
          if (newIndex >= gallery.items.length) {
            return gallery.items.length - 1;
          }
          return newIndex;
        });
      },
      [gallery.items.length],
    ),
  });

  if (gallery.imageCount === 0) {
    return (
      <Box
        sx={(theme) => ({
          gridColumn: "2",
          height: theme.spacing(6),
          background: "white",
        })}
      />
    );
  }

  return (
    <>
      <Box
        sx={{
          gridColumn: "2",
          position: "relative",
          display: "grid",
          gridTemplateRows: "1fr",
          overflow: "hidden",
          borderTopLeftRadius: theme.spacing(AD_DETAILS_BORDER_RADIUS),
          borderTopRightRadius: theme.spacing(AD_DETAILS_BORDER_RADIUS),
          [theme.breakpoints.down(AD_DETAILS_2_COLUMN_MAX_WIDTH)]: {
            gridColumn: "1",
            borderRadius: 0,
          },
          ...(isFullScreen && {
            position: "fixed",
            top: 0,
            left: 0,
            bottom: 0,
            right: 0,
            zIndex: 1300,
            backgroundColor: "black",
            borderRadius: 0,
          }),
        }}
      >
        <GalleryCarousel
          gallery={gallery}
          isFullScreen={isFullScreen}
          setIsFullScreen={handleSetIsFullScreen}
          isZoomed={isZoomed}
          setIsZoomed={setIsZoomed}
          activeIndex={activeIndex}
          setActiveIndex={setActiveIndex}
        />
        <ThumbnailsCarousel
          activeIndex={activeIndex}
          items={gallery.items}
          isFullScreen={isFullScreen}
          onThumbnailClicked={(index) => setActiveIndex(index)}
        />
      </Box>

      <Box
        sx={(theme) => ({
          flex: 1,
          display: "grid",
          gridTemplateColumns: "repeat(2, 1fr)",
          gridTemplateRows: "repeat(3, 1fr)",
          [theme.breakpoints.down(AD_DETAILS_2_COLUMN_MAX_WIDTH)]: {
            display: "none",
          },
        })}
      >
        {[...Array(6).keys()].map((index) => {
          const item = gallery.items[index + 1];
          return (
            <ThumbnailContainer key={index}>
              {item && (
                <GalleryThumbnail
                  onClick={() => {
                    setActiveIndex(index + 1);
                    handleSetIsFullScreen(true);
                  }}
                >
                  <Box
                    sx={{
                      background: `black url(${item.type === "video" ? item.thumbnail : item.preview})`,
                      backgroundSize: "cover",
                      backgroundPosition: "center",
                      position: "absolute",
                      top: "0",
                      width: "100%",
                      height: "100%",
                      overflow: "hidden",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      ...(item.type === "video" && item.preview
                        ? {
                            "&:hover": {
                              background: `url(${item.preview})`,
                              backgroundSize: "cover",
                              backgroundPosition: "center",
                              ".MuiIconButton-root": {
                                display: "none",
                              },
                            },
                          }
                        : {}),
                    }}
                  >
                    {item.type === "video" && (
                      <DarkIconButton>
                        <PlayArrowIcon
                          sx={{
                            fontSize: 40,
                          }}
                        />
                      </DarkIconButton>
                    )}
                  </Box>
                </GalleryThumbnail>
              )}
            </ThumbnailContainer>
          );
        })}
      </Box>
    </>
  );
}
